import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Crypto from 'expo-crypto';
import * as Device from 'expo-device';

export const DEVICE_OBSERVATION_VERSION = 1;
const INSTALL_ID_KEY = 'gab_install_id_v1';

let cachedObservation = null;
let inflight = null;
let cachedInstallId = null;

function bucketNumber(value, buckets) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return 'unknown';
    for (const edge of buckets) {
        if (n <= edge) return String(edge);
    }
    return `${buckets[buckets.length - 1]}plus`;
}

function screenClass(width, height) {
    const w = Number(width) || 0;
    const h = Number(height) || 0;
    const maxEdge = Math.max(w, h);
    if (maxEdge <= 0) return 'unknown';
    if (maxEdge < 800) return 'small';
    if (maxEdge < 1280) return 'medium';
    if (maxEdge < 1920) return 'large';
    return 'xlarge';
}

function osClass() {
    const os = Platform.OS || 'unknown';
    let major = 'unknown';
    try {
        const version = String(Platform.Version ?? Device.osVersion ?? '');
        const match = version.match(/^(\d+)/);
        if (match) major = match[1];
    } catch (_) { /* ignore */ }
    return `${os}-${major}`;
}

function deviceClass() {
    try {
        if (Platform.OS === 'web') {
            const w = typeof window !== 'undefined' ? window.innerWidth : 0;
            return w >= 1024 ? 'desktop' : 'mobile-web';
        }
        if (typeof Platform.isPad === 'boolean' && Platform.isPad) return 'tablet';
        if (Device.deviceType === 2) return 'tablet';
        return 'phone';
    } catch (_) {
        return 'unknown';
    }
}

function languageClass() {
    try {
        const lang = (typeof navigator !== 'undefined' && navigator.language)
            ? navigator.language
            : null;
        if (!lang) return 'unknown';
        return String(lang).split('-')[0].toLowerCase().slice(0, 8);
    } catch (_) {
        return 'unknown';
    }
}

function timezoneClass() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
    } catch (_) {
        return 'unknown';
    }
}

function randomId() {
    try {
        const bytes = Crypto.getRandomValues(new Uint8Array(16));
        return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    } catch (_) { /* older clients retain the existing random fallback */ }
    const hex = '0123456789abcdef';
    let id = '';
    for (let i = 0; i < 32; i++) id += hex[Math.floor(Math.random() * 16)];
    return id;
}

async function getOrCreateInstallId() {
    if (cachedInstallId) return cachedInstallId;
    try {
        if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
            let id = localStorage.getItem(INSTALL_ID_KEY);
            if (!id) {
                id = randomId();
                localStorage.setItem(INSTALL_ID_KEY, id);
            }
            if (localStorage.getItem(INSTALL_ID_KEY) === id) {
                cachedInstallId = id;
                return id;
            }
        }
    } catch (_) { /* ignore */ }
    try {
        let id = await AsyncStorage.getItem(INSTALL_ID_KEY);
        if (!id) {
            id = randomId();
            await AsyncStorage.setItem(INSTALL_ID_KEY, id);
        }
        if (await AsyncStorage.getItem(INSTALL_ID_KEY) !== id) return null;
        cachedInstallId = id;
        return id;
    } catch (_) {
        return null;
    }
}

function collectCanvasClass() {
    if (typeof document === 'undefined') return null;
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 120;
        canvas.height = 40;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillStyle = '#f60';
        ctx.fillRect(0, 0, 120, 40);
        ctx.fillStyle = '#069';
        ctx.fillText('gab.ai', 4, 8);
        return canvas.toDataURL().slice(0, 64);
    } catch (_) {
        return null;
    }
}

function collectWebglClass() {
    if (typeof document === 'undefined') return null;
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) return null;
        const debug = gl.getExtension('WEBGL_debug_renderer_info');
        const vendor = debug ? gl.getParameter(debug.UNMASKED_VENDOR_WEBGL) : '';
        const renderer = debug ? gl.getParameter(debug.UNMASKED_RENDERER_WEBGL) : '';
        return `${vendor}|${renderer}`.slice(0, 120);
    } catch (_) {
        return null;
    }
}

async function sha256Hex(value) {
    return Crypto.digestStringAsync(
        Crypto.CryptoDigestAlgorithm.SHA256,
        String(value || '')
    );
}

async function collectDeviceObservation() {
    const installId = await getOrCreateInstallId();
    const canvasClass = collectCanvasClass();
    const webglClass = collectWebglClass();
    const hw = (typeof navigator !== 'undefined') ? navigator.hardwareConcurrency : null;
    const mem = (typeof navigator !== 'undefined') ? navigator.deviceMemory : null;
    const screen = (typeof window !== 'undefined' && window.screen)
        ? { width: window.screen.width, height: window.screen.height }
        : { width: null, height: null };

    const components = {
        v: DEVICE_OBSERVATION_VERSION,
        tz: timezoneClass(),
        screen: screenClass(screen.width, screen.height),
        hw: bucketNumber(hw, [1, 2, 4, 8, 16]),
        mem: bucketNumber(mem, [2, 4, 8, 16]),
        lang: languageClass(),
        os: osClass(),
        device: deviceClass(),
        canvas: canvasClass ? await sha256Hex(canvasClass) : 'none',
        webgl: webglClass ? await sha256Hex(webglClass) : 'none',
    };

    const observationHash = await sha256Hex(JSON.stringify(components));
    const installIdHash = installId ? await sha256Hex(installId) : null;

    let confidence = 0.35;
    if (canvasClass) confidence += 0.25;
    if (webglClass) confidence += 0.2;
    if (installId) confidence += 0.15;
    if (Platform.OS !== 'web') confidence = Math.max(confidence, 0.45);
    confidence = Math.min(1, confidence);

    return {
        algorithmVersion: DEVICE_OBSERVATION_VERSION,
        observationHash,
        installIdHash,
        platform: Platform.OS || 'unknown',
        confidence: Math.round(confidence * 100) / 100,
    };
}

export function getCachedDeviceObservation() {
    return cachedObservation;
}

export function ensureDeviceObservation() {
    if (cachedObservation) return Promise.resolve(cachedObservation);
    if (inflight) return inflight;
    inflight = collectDeviceObservation()
        .then((obs) => {
            // Storage can recover later in this session. Retry missing identity.
            if (obs.installIdHash) cachedObservation = obs;
            return obs;
        })
        .catch(() => null)
        .finally(() => {
            inflight = null;
        });
    return inflight;
}

ensureDeviceObservation();
